import React, { useState, useEffect } from 'react';
import {
    AppBar,
    Toolbar,
    Button,
    Box,
    Container,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    styled,
    keyframes,
    Fab,
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import SaiSwagat from "../assets/SaiSwagat.png";
import {
    Info as AboutIcon,
    Home as HomeIcon,
    Message as MessageIcon,
    ContactSupport as ContactIcon,
    ShoppingCart as ShopIcon,
    RestaurantMenu as FullMenuIcon,
    People as CustomersIcon
} from '@mui/icons-material';

const pulseLogo = keyframes`
    0% { transform: scale(1); filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.21)) brightness(1.0) contrast(1.1); }
    50% { transform: scale(1.05); filter: drop-shadow(2px 2px 6px rgba(0,0,0,0.3)) brightness(1.1) contrast(1.2); }
    100% { transform: scale(1); filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.21)) brightness(1.0) contrast(1.1); }
`;

const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'isHomePage' && prop !== 'scrolled' && prop !== 'textColor',
})(({ theme, isHomePage, scrolled, textColor }) => ({
    position: 'fixed',
    backgroundColor: scrolled ? 'rgba(0, 0, 0, 0.6)' : 'transparent',
    color: textColor,
    boxShadow: scrolled ? theme.shadows[4] : 'none',
    transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
    backdropFilter: scrolled ? 'blur(1px)' : 'none',
    transform: 'translateZ(0)',
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    },
}));

const LogoLink = styled(Link)({
    textDecoration: 'none',
    '& img': {
        height: '50px',
        width: 'auto',
        position: 'fixed',
        top: '10px',
        left: '20px',
        zIndex: '1000',
        filter: 'drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.21)) brightness(1.0) contrast(1.1)',
        transition: 'all 0.3s ease',
        animation: `${pulseLogo} 3s infinite alternate`,
        '&:hover': {
            filter: 'drop-shadow(2px 2px 6px rgba(0,0,0,0.3)) brightness(1.1) contrast(1.2)',
        },
    },
});

const DesktopNavBox = styled(Box)(({ theme }) => ({
    display: 'none',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    [theme.breakpoints.up('md')]: {
        display: 'flex',
    },
}));

const MobileMenuButton = styled(IconButton)(({ theme }) => ({
    color: "black",
    [theme.breakpoints.up('md')]: {
        display: 'none',
    },
}));

const HeaderButton = styled(Button)(({ theme }) => ({
    color: "rgb(255, 255, 255)",
    textTransform: 'none',
    fontSize: '1.0rem',
    fontWeight: 500,
    '&:hover': {
        opacity: 0.8,
    },
}));

const MobileListItem = styled(ListItem)(({ theme }) => ({
    color: theme.palette.text.primary,
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const FloatingContactButton = styled(Fab)(({ theme }) => ({
    position: 'fixed',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    zIndex: 1500,
    '&:hover': {
        backgroundColor: theme.palette.secondary.dark,
    },
}));

const Header = () => {
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const isHomePage = location.pathname === '/';

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const navItems = [
        { text: 'Home', path: '/', icon: <HomeIcon /> },
        { text: 'Shop', path: '/shop', icon: <ShopIcon /> },
        { text: 'Full Menu', path: '/full-menu', icon: <FullMenuIcon /> },
        { text: 'About Us', path: '/about', icon: <AboutIcon /> },
        { text: 'Customers', path: '/customers', icon: <CustomersIcon /> },
        { text: 'Contact', path: '/contact', icon: <ContactIcon /> }
    ];

    const drawer = (
        <Box sx={{ width: 250 }} role="presentation">
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
                <IconButton onClick={handleDrawerToggle}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <Divider />
            <List>
                {navItems.map((item) => (
                    <MobileListItem
                        key={item.text}
                        button
                        component={Link}
                        to={item.path}
                        onClick={handleDrawerToggle}
                    >
                        <ListItemIcon>{item.icon}</ListItemIcon>
                        <ListItemText primary={item.text} />
                    </MobileListItem>
                ))}
            </List>
        </Box>
    );

    return (
        <>
            <StyledAppBar 
                position="fixed" 
                isHomePage={isHomePage} 
                scrolled={scrolled} 
                textColor="white" 
                sx={{
                    background: 'rgba(0, 0, 0, 0.196)',
                    backdropFilter:'blur(11px)',
                    boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.441)'
                }}
            >
                <Container maxWidth="lg">
                    <StyledToolbar>
                        <Box sx={{ flexGrow: 1 }}>
                            <LogoLink to="/">
                                <img src={SaiSwagat} alt="SaiSwagat Logo" />
                            </LogoLink>
                        </Box>

                        <DesktopNavBox>
                            {navItems.map((item) => (
                                <HeaderButton
                                    key={item.text}
                                    component={Link}
                                    to={item.path}
                                    startIcon={item.icon}
                                >
                                    {item.text}
                                </HeaderButton>
                            ))}
                        </DesktopNavBox>

                        <MobileMenuButton
                            aria-label="open menu"
                            edge="end"
                            onClick={handleDrawerToggle}
                        >
                            <MenuIcon />
                        </MobileMenuButton>
                    </StyledToolbar>
                </Container>
            </StyledAppBar>

            <FloatingContactButton
                component={Link}
                to="/contact"
                aria-label="contact"
            >
                <MessageIcon />
            </FloatingContactButton>

            <Drawer
                anchor="right"
                variant="temporary"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{
                    keepMounted: true,
                }}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250 },
                }}
            >
                {drawer}
            </Drawer>
        </>
    );
};

export default Header;
